// src/pages/appointment/AppointmentForm.js
// Componente de formulario para crear/editar citas

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Row, Col, Card, Button, Spinner, Modal, Table, Alert, Badge } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { getPets, getPetsByClientId, createPet } from '../../services/petApi';
import { getCustomers, createCustomer } from '../../services/customerApi';
import { getGroomingItems, getAppointmentStatuses, getLastAppointmentsByClient } from '../../services/appointmentApi';
import { getGroomers } from '../../services/employeeApi';
import { getAppointmentDetails } from '../../services/appointmentDetailApi';
import { dotoniwoofApi } from '../../api/dotoniwooApi';
import CustomerForm from '../customer/CustomerForm';
import PetForm from '../pet/PetForm';
import { useAuth } from '../../context/AuthContext';
import { storage } from '../../firebase/firebaseConfig';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import './AppointmentForm.css';

// Detectar si el estado seleccionado es PAGADA (por nombre, no por ID fijo)
const isStatusPagada = (statusOptions, idStatus) => {
  const selected = statusOptions.find((s) => String(s.id_status) === String(idStatus));
  const name = (selected?.status_name || selected?.name || '').toUpperCase().trim();
  return name === 'PAGADA';
};

const AppointmentForm = ({
  onSubmit,
  loading = false,
  initialData = null,
  selectedDate = null,
  defaultTime = null,
  /** Citas del día para calcular horarios disponibles (se excluye la cita en edición) */
  appointmentsForDate = [],
  submitText = "Agendar Cita",
  /** Callback para mostrar alertas (opcional) */
  onAlert = null
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    id_pet: initialData?.id_pet || '',
    id_item: initialData?.id_item || '',
    id_employee: initialData?.id_employee ?? '',
    start_at: initialData?.start_at || '',
    duration_min: initialData?.duration_min || 30,
    id_status: initialData?.id_status || 12,
    create_by_person: initialData?.create_by_person || 1
  });

  // Estados para modales de selección
  const [showPetModal, setShowPetModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [pets, setPets] = useState([]);
  const [items, setItems] = useState([]);
  const [petSearch, setPetSearch] = useState('');
  const [clientIdSearch, setClientIdSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [petLoading, setPetLoading] = useState(false);
  const [itemLoading, setItemLoading] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  /** Carrito de servicios: solo en modo creación. Cada elemento { id_item, quantity, unit_price, description } */
  const [cartItems, setCartItems] = useState([]);
  const [cartDuplicateMsg, setCartDuplicateMsg] = useState('');
  const [cartJustAdded, setCartJustAdded] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [groomers, setGroomers] = useState([]);
  const [searchByClientId, setSearchByClientId] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [selectedPaymentMethodModal, setSelectedPaymentMethodModal] = useState('');

  // Estados para link "últimos servicios" y botón "crear cliente"
  const [showClientServicesModal, setShowClientServicesModal] = useState(false);
  const [clientServicesData, setClientServicesData] = useState([]);
  const [clientServicesLoading, setClientServicesLoading] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showPostCreateOptionsModal, setShowPostCreateOptionsModal] = useState(false);
  const [newlyCreatedClient, setNewlyCreatedClient] = useState(null);
  const [showCreatePetModal, setShowCreatePetModal] = useState(false);
  const [createClientLoading, setCreateClientLoading] = useState(false);
  const [createPetLoading, setCreatePetLoading] = useState(false);

  // Estados de tiempo (en creación usa defaultTime si se pasa)
  const getInitialTime = () => {
    if (initialData?.start_at) {
      const d = new Date(initialData.start_at);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return defaultTime || '09:00';
  };
  const [selectedTime, setSelectedTime] = useState(getInitialTime);

  // Estados para imágenes (solo en modo edición)
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [beforeImagePreview, setBeforeImagePreview] = useState(initialData?.before_photo_url || '');
  const [afterImagePreview, setAfterImagePreview] = useState(initialData?.after_photo_url || '');
  const [uploadingBefore, setUploadingBefore] = useState(false);
  const [uploadingAfter, setUploadingAfter] = useState(false);
  const [beforeImageUrl, setBeforeImageUrl] = useState(initialData?.before_photo_url || '');
  const [afterImageUrl, setAfterImageUrl] = useState(initialData?.after_photo_url || '');
  const [uploadError, setUploadError] = useState('');

  // Opciones de duración
  const durationOptions = [
    { value: 30, label: '30 minutos' },
    { value: 60, label: '1 hora' },
    { value: 90, label: '1.5 horas' },
    { value: 120, label: '2 horas' },
    { value: 180, label: '3 horas' }
  ];

  useEffect(() => {
    loadStatuses();
  }, [initialData]);

  // Cargar groomers para selector
  useEffect(() => {
    const load = async () => {
      const { ok, data } = await getGroomers();
      if (ok && (data?.code === 200 || data?.data)) {
        const list = data.data || data.message || [];
        setGroomers(Array.isArray(list) ? list : []);
      }
    };
    load();
  }, []);

  // Cargar métodos de pago cuando se edita o cuando se abre el modal de pago
  useEffect(() => {
    if (!initialData && !showPaymentMethodModal) return;
    const load = async () => {
      try {
        const { data } = await dotoniwoofApi.get('/paymentMethod.php/sales');
        if (data?.code === 200 && Array.isArray(data.message)) {
          setPaymentMethods(data.message);
        } else {
          const res = await dotoniwoofApi.get('/paymentMethod.php');
          if (res.data?.code === 200 && Array.isArray(res.data.message)) {
            setPaymentMethods(res.data.message);
          }
        }
      } catch {
        try {
          const { data } = await dotoniwoofApi.get('/paymentMethod.php');
          if (data?.code === 200) setPaymentMethods(data.message || []);
        } catch (_) {}
      }
    };
    load();
  }, [initialData, showPaymentMethodModal]);

  // Generar todas las horas posibles (9:00 a 18:00, cada 30 min)
  const allTimeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 9; hour <= 18; hour++) {
      slots.push(`${String(hour).padStart(2, '0')}:00`);
      if (hour < 18) slots.push(`${String(hour).padStart(2, '0')}:30`);
    }
    return slots;
  }, []);

  // Horarios disponibles: excluir los que se solapan con citas existentes del día
  const availableTimeSlots = useMemo(() => {
    if (!selectedDate || !appointmentsForDate?.length) return allTimeSlots;
    const durationMin = parseInt(formData.duration_min, 10) || 60;
    const excludeId = initialData?.id_appointment ?? null;
    const dayStart = new Date(selectedDate + 'T00:00:00').getTime();

    const toMinutes = (dateStr) => {
      const d = new Date(dateStr);
      return (d.getTime() - dayStart) / 60000;
    };

    const occupied = appointmentsForDate
      .filter((apt) => apt.id_appointment !== excludeId && apt.start_at && apt.duration_min)
      .map((apt) => ({
        start: toMinutes(apt.start_at),
        end: toMinutes(apt.start_at) + (parseInt(apt.duration_min, 10) || 60)
      }));

    return allTimeSlots.filter((timeStr) => {
      const [h, m] = timeStr.split(':').map(Number);
      const slotStart = h * 60 + m;
      const slotEnd = slotStart + durationMin;
      const overlaps = occupied.some(({ start, end }) => start < slotEnd && end > slotStart);
      return !overlaps;
    });
  }, [selectedDate, appointmentsForDate, formData.duration_min, initialData?.id_appointment, allTimeSlots]);

  // Si el horario seleccionado ya no está disponible, elegir el primero disponible
  useEffect(() => {
    if (availableTimeSlots.length > 0 && !availableTimeSlots.includes(selectedTime)) {
      setSelectedTime(availableTimeSlots[0]);
    }
  }, [availableTimeSlots, selectedTime]);

  const timeSlotsToShow = availableTimeSlots.length > 0 ? availableTimeSlots : allTimeSlots;

  // Cargar mascotas cuando se abre el modal
  useEffect(() => {
    if (showPetModal) {
      loadPets();
    }
  }, [showPetModal]);

  // Cargar items cuando se abre el modal
  useEffect(() => {
    if (showItemModal) {
      loadItems();
    }
  }, [showItemModal]);

  // Actualizar fecha y hora cuando cambie selectedDate
  useEffect(() => {
    if (selectedDate) {
      const dateTime = `${selectedDate} ${selectedTime}:00`;
      setFormData(prev => ({
        ...prev,
        start_at: dateTime
      }));
    }
  }, [selectedDate, selectedTime]);

  // Cita finalizada: no se permite editar carrito de servicios
  const isCompleted = initialData && (
    initialData.status_name === 'COMPLETADO' ||
    String(initialData.id_status) === '15'
  );
  const useCartInEdit = initialData && !isCompleted;

  // Pre-seleccionar mascota si se pasa en initialData
  useEffect(() => {
    if (initialData?.pet && initialData.pet.id_pet) {
      setSelectedPet(initialData.pet);
      setFormData(prev => ({
        ...prev,
        id_pet: initialData.pet.id_pet
      }));
    } else if (initialData?.id_pet) {
      setFormData(prev => ({
        ...prev,
        id_pet: initialData.id_pet
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Cerrar modal de servicios cuando cambia el cliente
  const prevClientIdRef = useRef(selectedPet?.id_client);
  useEffect(() => {
    const currentClientId = selectedPet?.id_client;
    const prevClientId = prevClientIdRef.current;
    
    // Si el cliente cambió y el modal está abierto, cerrarlo y limpiar datos
    if (showClientServicesModal && prevClientId !== undefined && currentClientId !== prevClientId) {
      setShowClientServicesModal(false);
      setClientServicesData([]);
    }
    
    // Actualizar la referencia
    prevClientIdRef.current = currentClientId;
  }, [selectedPet?.id_client, showClientServicesModal]);

  // En edición no finalizada: cargar servicios actuales y meterlos en el carrito
  useEffect(() => {
    if (!useCartInEdit || !initialData?.id_appointment) return;
    const load = async () => {
      const [detailsRes, itemsRes] = await Promise.all([
        getAppointmentDetails(initialData.id_appointment),
        getGroomingItems()
      ]);
      const itemsMap = {};
      if (itemsRes?.ok && itemsRes?.data?.code === 200) {
        const list = itemsRes.data.data || itemsRes.data.message || [];
        (Array.isArray(list) ? list : []).forEach((it) => {
          if (it.id_item) itemsMap[it.id_item] = it;
        });
      }
      if (detailsRes?.ok && detailsRes?.data?.code === 200) {
        const raw = detailsRes.data.data || detailsRes.data.message || [];
        const details = Array.isArray(raw) ? raw : [];
        const cart = details.map((d) => {
          const it = itemsMap[d.id_item] || {};
          return {
            id_item: d.id_item,
            quantity: d.quantity ?? 1,
            unit_price: parseFloat(d.unit_price) || parseFloat(it.price) || 0,
            description: d.description || it.description || it.name || 'Servicio',
            id_appointment_detail: d.id_appointment_detail
          };
        });
        setCartItems(cart);
        if (cart.length && !selectedItem) {
          setFormData((prev) => ({ ...prev, id_item: cart[0].id_item }));
        }
      } else if (initialData?.id_item) {
        const it = itemsMap[initialData.id_item] || {};
        setCartItems([{
          id_item: initialData.id_item,
          quantity: 1,
          unit_price: parseFloat(initialData.unit_price) || parseFloat(it.price) || 0,
          description: it.description || it.name || 'Servicio'
        }]);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id_appointment, useCartInEdit]);

  // Cargar mascotas
  const loadPets = async () => {
    try {
      setPetLoading(true);
      const { ok, data } = await getPets({ page: 1, per_page: 100, q: petSearch });
      
      if (ok && data?.code === 200) {
        const responseData = data.data || data.message || [];
        setPets(responseData.pets || responseData);
      }
    } catch (error) {
      console.error('Error cargando mascotas:', error);
    } finally {
      setPetLoading(false);
    }
  };

  // Buscar mascotas por identificación de cliente
  const searchPetsByClientId = async () => {
    if (!clientIdSearch.trim()) {
      return;
    }

    try {
      setPetLoading(true);
      
      // Primero buscar el cliente por identificación (documento)
      const { ok: customersOk, data: customersData } = await getCustomers({ 
        page: 1, 
        per_page: 10, 
        q: clientIdSearch.trim() 
      });

      if (customersOk && customersData?.code === 200) {
        const responseData = customersData.data || customersData.message || [];
        const clients = responseData.customers || responseData;
        
        if (clients.length === 0) {
          setPets([]);
          alert('No se encontró ningún cliente con esa identificación');
          return;
        }

        // Buscar el cliente que coincida exactamente con el documento ingresado
        let client = null;
        for (const c of clients) {
          // Verificar si el documento del cliente coincide con la búsqueda
          const clientDocument = c.person?.document || c.document || '';
          if (clientDocument.toString().trim() === clientIdSearch.trim()) {
            client = c;
            break;
          }
        }
        
        // Si no encontramos coincidencia exacta, usar el primero
        if (!client) {
          client = clients[0];
        }
        
        // Verificar que el cliente tenga id_client
        if (!client.id_client) {
          console.error('Cliente encontrado pero sin id_client:', client);
          setPets([]);
          alert('Error: El cliente encontrado no tiene un ID válido');
          return;
        }
        
        console.log('Cliente encontrado:', client);
        console.log('Buscando mascotas para cliente ID:', client.id_client);
        
        // Obtener las mascotas del cliente usando su ID
        const { ok: petsOk, data: petsData } = await getPetsByClientId(client.id_client);
        
        if (petsOk && petsData?.code === 200) {
          const petsResponseData = petsData.data || petsData.message || [];
          setPets(Array.isArray(petsResponseData) ? petsResponseData : []);
          setSearchByClientId(true);
        } else {
          setPets([]);
          alert('No se encontraron mascotas para este cliente');
        }
      } else {
        setPets([]);
        alert('Error al buscar el cliente');
      }
    } catch (error) {
      console.error('Error buscando mascotas por cliente:', error);
      alert('Error al buscar mascotas por identificación de cliente');
    } finally {
      setPetLoading(false);
    }
  };

  // Cargar items de grooming
  const loadItems = async () => {
    try {
      setItemLoading(true);
      const { ok, data } = await getGroomingItems();
      
      if (ok && data?.code === 200) {
        const responseData = data.data || data.message || [];
        setItems(responseData);
      }
    } catch (error) {
      console.error('Error cargando items:', error);
    } finally {
      setItemLoading(false);
    }
  };

  // Cargar estados de agenda
  const loadStatuses = async () => {
    const defaultStatuses = [
      { id_status: 12, status_name: 'PROGRAMADO' },
      { id_status: 13, status_name: 'CONFIRMADO' },
      { id_status: 14, status_name: 'EN PROCESO' },
      { id_status: 15, status_name: 'COMPLETADO' },
      { id_status: 16, status_name: 'CANCELADO' },
      { id_status: 5, status_name: 'PAGADA' }
    ];
    const ensurePagada = (list) => {
      const arr = Array.isArray(list) ? [...list] : [];
      const hasPagada = arr.some((s) => (s.status_name || s.name || '').toUpperCase() === 'PAGADA');
      if (!hasPagada) arr.push({ id_status: 5, status_name: 'PAGADA' });
      return arr;
    };
    // Si estamos editando (initialData existe), cargamos todos los estados
    if (initialData) {
      try {
        const { ok, data } = await getAppointmentStatuses();
        
        if (ok && data?.code === 200) {
          const responseData = data.data || data.message || [];
          setStatusOptions(ensurePagada(responseData));
        } else {
          setStatusOptions(defaultStatuses);
        }
      } catch (error) {
        console.error('Error cargando estados:', error);
        setStatusOptions(defaultStatuses);
      }
    } else {
      // Si estamos creando, solo mostramos el estado PROGRAMADO
      setStatusOptions([
        { id_status: 12, status_name: 'PROGRAMADO' }
      ]);
    }
  };

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    console.log('e', e);
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Manejar cambio de tiempo
  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    setSelectedTime(newTime);
    if (selectedDate) {
      const dateTime = `${selectedDate} ${newTime}:00`;
      setFormData(prev => ({
        ...prev,
        start_at: dateTime
      }));
    }
  };

  // Abrir modal de selección de mascota
  const handleOpenPetModal = () => {
    setShowPetModal(true);
    setPetSearch('');
    setClientIdSearch('');
    setSearchByClientId(false);
  };

  // Cerrar modal de selección de mascota
  const handleClosePetModal = () => {
    setShowPetModal(false);
    setPetSearch('');
    setClientIdSearch('');
    setSearchByClientId(false);
  };

  // Seleccionar mascota
  const handleSelectPet = (pet) => {
    setSelectedPet(pet);
    setFormData(prev => ({
      ...prev,
      id_pet: pet.id_pet
    }));
    setShowPetModal(false);
  };

  // Abrir modal de selección de item
  const handleOpenItemModal = () => {
    setShowItemModal(true);
    setItemSearch('');
  };

  // Cerrar modal de selección de item
  const handleCloseItemModal = () => {
    setShowItemModal(false);
    setItemSearch('');
    setCartDuplicateMsg('');
  };

  // Seleccionar item (modo edición: un solo servicio)
  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setFormData(prev => ({
      ...prev,
      id_item: item.id_item
    }));
    setShowItemModal(false);
  };

  // Añadir servicio al carrito (modo creación: no permite repetidos). Cierra modal y anima el carrito.
  const handleAddToCart = (item) => {
    if (cartItems.some((x) => x.id_item === item.id_item)) {
      setCartDuplicateMsg('Este servicio ya está en el carrito.');
      return;
    }
    setCartDuplicateMsg('');
    const price = parseFloat(item.price) || 0;
    setCartItems((prev) => [
      ...prev,
      {
        id_item: item.id_item,
        quantity: 1,
        unit_price: price,
        description: item.description || item.name || 'Servicio'
      }
    ]);
    setShowItemModal(false);
    setCartJustAdded(true);
    setTimeout(() => setCartJustAdded(false), 600);
  };

  // Quitar línea del carrito
  const handleRemoveFromCart = (index) => {
    setCartItems(prev => prev.filter((_, i) => i !== index));
  };

  // Buscar mascotas
  const handlePetSearch = (e) => {
    setPetSearch(e.target.value);
    setClientIdSearch('');
    setSearchByClientId(false);
    setTimeout(() => {
      loadPets();
    }, 500);
  };

  // Buscar por identificación de cliente
  const handleClientIdSearch = (e) => {
    setClientIdSearch(e.target.value);
  };

  // Abrir modal de últimos servicios del cliente
  const handleOpenClientServicesModal = async () => {
    if (!selectedPet?.id_client) return;
    setShowClientServicesModal(true);
    setClientServicesLoading(true);
    setClientServicesData([]);
    try {
      // Buscar por cliente y mascota usando el endpoint con ambos parámetros
      const { ok, data } = await getLastAppointmentsByClient(
        selectedPet.id_client,
        selectedPet.id_pet,
        10
      );
      if (ok && (data?.code === 200 || data?.data)) {
        const list = data.data || data.message || [];
        setClientServicesData(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      console.error('Error cargando servicios del cliente:', error);
    } finally {
      setClientServicesLoading(false);
    }
  };

  // Crear cliente
  const handleCreateClient = async (customerData) => {
    try {
      setCreateClientLoading(true);
      const { ok, status, data } = await createCustomer(customerData);
      if (ok && (status === 201 || data?.code === 201)) {
        const createdClient = data?.data || data?.message || data;
        setShowCreateClientModal(false);
        setNewlyCreatedClient(createdClient);
        setShowPostCreateOptionsModal(true);
        if (onAlert) onAlert('Cliente creado exitosamente', 'success');
        else alert('Cliente creado exitosamente');
      } else {
        const msg = data?.message || 'Error creando cliente';
        if (onAlert) onAlert(msg, 'error');
        else alert(msg);
      }
    } catch (error) {
      console.error('Error creando cliente:', error);
      const msg = error.response?.data?.message || 'Error inesperado al crear cliente';
      if (onAlert) onAlert(msg, 'error');
      else alert(msg);
    } finally {
      setCreateClientLoading(false);
    }
  };

  // Crear mascota (desde modal post-crear cliente)
  const handleCreatePetFromNewClient = async (petData) => {
    if (!newlyCreatedClient?.id_client) return;
    try {
      setCreatePetLoading(true);
      const petDataWithClient = { ...petData, id_client: newlyCreatedClient.id_client };
      const { ok, status, data } = await createPet(petDataWithClient);
      if (ok && (status === 201 || data?.code === 201)) {
        setShowCreatePetModal(false);
        setShowPostCreateOptionsModal(false);
        setNewlyCreatedClient(null);
        if (onAlert) onAlert('Mascota creada exitosamente', 'success');
        else alert('Mascota creada exitosamente');
        loadPets();
      } else {
        const msg = data?.message || 'Error creando mascota';
        if (onAlert) onAlert(msg, 'error');
        else alert(msg);
      }
    } catch (error) {
      console.error('Error creando mascota:', error);
      const msg = error.response?.data?.message || 'Error inesperado al crear mascota';
      if (onAlert) onAlert(msg, 'error');
      else alert(msg);
    } finally {
      setCreatePetLoading(false);
    }
  };

  // Cerrar modal de opciones post-crear cliente
  const handleClosePostCreateOptions = () => {
    setShowPostCreateOptionsModal(false);
    setNewlyCreatedClient(null);
  };

  // Crear cita desde modal opciones (solo cerrar el modal, el usuario ya está en el form)
  const handlePostCreateChooseCita = () => {
    setShowPostCreateOptionsModal(false);
    setNewlyCreatedClient(null);
  };

  // Ejecutar búsqueda por identificación de cliente
  const handleSearchByClientId = () => {
    if (clientIdSearch.trim()) {
      searchPetsByClientId();
    }
  };

  // Buscar items
  const handleItemSearch = (e) => {
    setItemSearch(e.target.value);
    // Filtrar items localmente
    const filtered = items.filter(item => 
      item.name?.toLowerCase().includes(e.target.value.toLowerCase()) ||
      item.description?.toLowerCase().includes(e.target.value.toLowerCase())
    );
    setItems(filtered);
  };

  // Formatear precio con signo de peso colombiano
  const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return '';
    return `$${numPrice.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Manejar selección de imagen antes del baño
  const handleBeforeImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBeforeImage(file);
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setBeforeImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Subir inmediatamente a Firebase
      uploadImageToFirebase(file, 'before');
    }
  };

  // Manejar selección de imagen después del baño
  const handleAfterImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAfterImage(file);
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAfterImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Subir inmediatamente a Firebase
      uploadImageToFirebase(file, 'after');
    }
  };

  // Subir imagen a Firebase Storage
  const uploadImageToFirebase = (file, type) => {
    const setUploading = type === 'before' ? setUploadingBefore : setUploadingAfter;
    const setImageUrl = type === 'before' ? setBeforeImageUrl : setAfterImageUrl;
    
    setUploading(true);
    setUploadError('');

    const newFileName = `appointment_${type}_${Date.now()}.${file.name.split('.').pop()}`;
    const storageRef = ref(storage, `appointments/${newFileName}`);
    const metadata = {
      contentType: file.type,
    };

    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log(`Upload ${type} is ${progress}% done`);
      },
      (error) => {
        console.error('Error uploading image:', error);
        setUploadError(`Error al subir imagen ${type === 'before' ? 'antes' : 'después'}: ${error.message}`);
        setUploading(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          console.log(`${type} image available at:`, downloadURL);
          setImageUrl(downloadURL);
          setUploading(false);
        });
      }
    );
  };

  // Validar formulario
  const validateForm = () => {
    if (!formData.id_pet) return 'Debes seleccionar una mascota';
    if (useCartInEdit || !initialData) {
      if (!cartItems.length) return 'Debes añadir al menos un servicio al carrito';
    } else if (initialData) {
      if (!formData.id_item) return 'Debes seleccionar un servicio';
    }
    if (!formData.start_at) return 'Debes seleccionar fecha y hora';
    if (!formData.duration_min) return 'Debes seleccionar la duración';
    const isPagada = isStatusPagada(statusOptions, formData.id_status);
    if (isPagada && !user?.uid) return 'Debes iniciar sesión para registrar la venta';
    return null;
  };

  // Construir payload para el backend
  const buildPayload = (paymentMethodIdOverride = null) => {
    const payload = {
      id_pet: parseInt(formData.id_pet),
      id_item: (useCartInEdit && cartItems.length) ? cartItems[0].id_item : (initialData ? parseInt(formData.id_item) : (cartItems[0]?.id_item ?? 0)),
      start_at: formData.start_at,
      duration_min: parseInt(formData.duration_min),
      id_status: parseInt(formData.id_status),
      create_by_person: parseInt(formData.create_by_person)
    };
    if (formData.id_employee) payload.id_employee = parseInt(formData.id_employee);
    const isPagada = isStatusPagada(statusOptions, formData.id_status);
    if (isPagada) {
      const idPm = paymentMethodIdOverride ?? formData.id_payment_method;
      payload.id_payment_method = parseInt(idPm, 10);
      payload.firebase_uid = user?.uid || '';
    }
    if ((!initialData || useCartInEdit) && cartItems.length) {
      payload.items = cartItems.map(({ id_item, quantity, unit_price }) => ({
        id_item,
        quantity: quantity ?? 1,
        unit_price: unit_price ?? 0
      }));
    }
    if (initialData) {
      if (beforeImageUrl) payload.before_photo_url = beforeImageUrl;
      if (afterImageUrl) payload.after_photo_url = afterImageUrl;
    }
    return payload;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm();
    if (validation) {
      if (onAlert) onAlert(validation, 'error');
      else alert(validation);
      return;
    }

    const isPagada = isStatusPagada(statusOptions, formData.id_status);
    if (isPagada) {
      setSelectedPaymentMethodModal('');
      setShowPaymentMethodModal(true);
      return;
    }

    const payload = buildPayload();
    await onSubmit(payload);
  };

  // Confirmar pago y generar venta (desde modal)
  const handleConfirmPaymentAndGenerateSale = async () => {
    if (!selectedPaymentMethodModal) {
      if (onAlert) onAlert('Debes seleccionar un método de pago', 'error');
      else alert('Debes seleccionar un método de pago');
      return;
    }
    setShowPaymentMethodModal(false);
    const payload = buildPayload(selectedPaymentMethodModal);
    await onSubmit(payload);
    setSelectedPaymentMethodModal('');
  };

  return (
    <>
      <Form onSubmit={handleSubmit} className="appointment-form-modern">
        {/* Sección: Mascota y Servicios */}
        <div className="form-section">
          <div className="form-section-title">
            <span className="section-icon"><i className="fas fa-paw"></i></span>
            Quién y qué
          </div>
          <Row>
            <Col md={6}>
              <div className="field-group">
                <label className="form-label-modern">Mascota *</label>
                <div className="select-block">
                  <Form.Control
                    type="text"
                    value={selectedPet ? `${selectedPet.name} — ${selectedPet.name_client}` : ''}
                    placeholder="Elige una mascota"
                    readOnly
                    onClick={handleOpenPetModal}
                  />
                  <Button type="button" className="btn-select-modern" onClick={handleOpenPetModal}>
                    <i className="fas fa-search"></i> Buscar
                  </Button>
                </div>
                {formData.id_pet && <Form.Text className="text-muted small">ID: {formData.id_pet}</Form.Text>}
                <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
                  {selectedPet?.id_client && (
                    <Button
                      variant="link"
                      className="p-0 align-baseline text-info small"
                      onClick={handleOpenClientServicesModal}
                    >
                      <i className="fas fa-history me-1"></i>
                      Ver últimos servicios del cliente
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowCreateClientModal(true)}
                  >
                    <i className="fas fa-user-plus me-1"></i>
                    Crear cliente
                  </Button>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="field-group">
                <label className="form-label-modern">
                  {useCartInEdit ? 'Servicios' : initialData ? 'Servicio' : 'Servicios'}
                </label>
                {initialData && isCompleted ? (
                  <div className="select-block">
                    <Form.Control
                      type="text"
                      value={selectedItem ? selectedItem.description : ''}
                      placeholder="Servicio"
                      readOnly
                    />
                    <Button type="button" className="btn-select-modern" onClick={handleOpenItemModal}>
                      <i className="fas fa-search"></i> Buscar
                    </Button>
                  </div>
                ) : (
                  <motion.div
                    className="cart-block"
                    animate={cartJustAdded ? { scale: [1, 1.02, 1] } : {}}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Button type="button" className="btn-add-service" onClick={handleOpenItemModal}>
                        <i className="fas fa-plus me-1"></i>
                        Añadir servicio
                      </Button>
                      {cartItems.length > 0 && (
                        <Badge bg="primary" className="rounded-pill">{cartItems.length}</Badge>
                      )}
                    </div>
                    <AnimatePresence>
                      {cartItems.length > 0 && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <Table size="sm" bordered responsive className="mb-0">
                            <thead>
                              <tr>
                                <th>Servicio</th>
                                <th className="text-center">Cant.</th>
                                <th className="text-end">P. unit.</th>
                                <th className="text-end">Subtotal</th>
                                <th style={{ width: 44 }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {cartItems.map((row, idx) => (
                                <motion.tr key={`${row.id_item}-${idx}`} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.03 }}>
                                  <td>{row.description}</td>
                                  <td className="text-center">{row.quantity}</td>
                                  <td className="text-end">{formatPrice(row.unit_price)}</td>
                                  <td className="text-end">{formatPrice((row.quantity || 1) * (row.unit_price || 0))}</td>
                                  <td>
                                    <Button variant="outline-danger" size="sm" type="button" onClick={() => handleRemoveFromCart(idx)} title="Quitar">
                                      <i className="fas fa-trash"></i>
                                    </Button>
                                  </td>
                                </motion.tr>
                              ))}
                            </tbody>
                          </Table>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </div>
            </Col>
          </Row>
        </div>

        {/* Sección: Fecha y hora */}
        <div className="form-section">
          <div className="form-section-title">
            <span className="section-icon"><i className="fas fa-calendar-alt"></i></span>
            Cuándo
          </div>
          <div className="schedule-block">
            <div className="schedule-field">
              <label className="form-label-modern">Fecha</label>
              <Form.Control type="date" value={selectedDate || ''} readOnly className="bg-white" />
            </div>
            <div className="schedule-field">
              <label className="form-label-modern">Hora *</label>
              <Form.Select value={selectedTime} onChange={handleTimeChange}>
                {timeSlotsToShow.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </Form.Select>
            </div>
            <div className="schedule-field">
              <label className="form-label-modern">Duración *</label>
              <Form.Select name="duration_min" value={formData.duration_min} onChange={handleChange}>
                {durationOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </div>
          </div>
        </div>

        {/* Sección: Estado y Groomer */}
        <div className="form-section">
          <div className="form-section-title">
            <span className="section-icon"><i className="fas fa-user-check"></i></span>
            Asignación
          </div>
          <div className="assign-block">
            <div className="field-group">
              <label className="form-label-modern">Estado</label>
              <Form.Select name="id_status" value={formData.id_status} onChange={handleChange}>
                {statusOptions.map(s => (
                  <option key={s.id_status} value={s.id_status}>{s.status_name}</option>
                ))}
              </Form.Select>
            </div>
            <div className="field-group">
              <label className="form-label-modern">Groomer</label>
              <Form.Select name="id_employee" value={formData.id_employee} onChange={handleChange}>
                <option value="">Sin asignar</option>
                {groomers.map((g) => (
                  <option key={g.id_employee} value={g.id_employee}>
                    {g.full_name || `${g.name || ''} ${g.surname || ''}`.trim() || `Empleado ${g.id_employee}`}
                  </option>
                ))}
              </Form.Select>
              {formData.id_employee && groomers.find((g) => g.id_employee === parseInt(formData.id_employee, 10))?.agenda_color && (
                <span
                  className="groomer-chip"
                  style={{ backgroundColor: groomers.find((g) => g.id_employee === parseInt(formData.id_employee, 10))?.agenda_color }}
                >
                  Color en agenda
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Sección Fotos - Solo en edición */}
        {initialData && (
          <div className="form-section photos-section">
            <div className="form-section-title">
              <span className="section-icon"><i className="fas fa-camera"></i></span>
              Fotos del servicio
            </div>
            {uploadError && (
              <Alert variant="danger" dismissible onClose={() => setUploadError('')} className="mb-3">
                {uploadError}
              </Alert>
            )}
            <Row>
              <Col md={6}>
                <div className="field-group">
                  <label className="form-label-modern"><i className="fas fa-dog me-1 text-muted"></i> Antes del baño</label>
                  <div className="photo-upload-box">
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleBeforeImageChange}
                      disabled={uploadingBefore}
                    />
                    {uploadingBefore && <div className="mt-2"><Spinner animation="border" size="sm" className="me-2" /><small>Subiendo...</small></div>}
                    {beforeImagePreview && (
                      <div className="mt-2">
                        <img src={beforeImagePreview} alt="Antes" className="photo-preview w-100" />
                        {beforeImageUrl && <Badge bg="success" className="mt-2"><i className="fas fa-check me-1"></i> Guardada</Badge>}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="field-group">
                  <label className="form-label-modern"><i className="fas fa-spa me-1 text-muted"></i> Después del baño</label>
                  <div className="photo-upload-box">
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleAfterImageChange}
                      disabled={uploadingAfter}
                    />
                    {uploadingAfter && <div className="mt-2"><Spinner animation="border" size="sm" className="me-2" /><small>Subiendo...</small></div>}
                    {afterImagePreview && (
                      <div className="mt-2">
                        <img src={afterImagePreview} alt="Después" className="photo-preview w-100" />
                        {afterImageUrl && <Badge bg="success" className="mt-2"><i className="fas fa-check me-1"></i> Guardada</Badge>}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
            <Alert variant="info" className="mb-0 small py-2">
              <i className="fas fa-info-circle me-2"></i>
              Las imágenes se suben automáticamente. Puedes seguir editando mientras se suben.
            </Alert>
          </div>
        )}

        <Button
          type="submit"
          className="btn-submit-modern"
          disabled={loading || uploadingBefore || uploadingAfter}
        >
          {loading ? (
            <><Spinner animation="border" size="sm" /> Procesando...</>
          ) : uploadingBefore || uploadingAfter ? (
            <><Spinner animation="border" size="sm" /> Subiendo imágenes...</>
          ) : (
            <><i className="fas fa-calendar-check me-2"></i>{submitText}</>
          )}
        </Button>
      </Form>

      {/* Modal de selección de mascota */}
      <Modal show={showPetModal} onHide={handleClosePetModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Seleccionar Mascota</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label>Buscar por nombre o cliente</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar mascota por nombre o cliente..."
                value={petSearch}
                onChange={handlePetSearch}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Buscar por identificación de cliente</Form.Label>
              <div className="d-flex">
                <Form.Control
                  type="text"
                  placeholder="Ingrese identificación del cliente..."
                  value={clientIdSearch}
                  onChange={handleClientIdSearch}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchByClientId();
                    }
                  }}
                />
                <Button
                  variant="primary"
                  onClick={handleSearchByClientId}
                  className="ms-2"
                  disabled={!clientIdSearch.trim() || petLoading}
                >
                  <i className="fas fa-search"></i>
                </Button>
              </div>
            </Col>
          </Row>
          {searchByClientId && (
            <Alert variant="info" className="mb-3">
              <i className="fas fa-info-circle me-2"></i>
              Mostrando mascotas del cliente con identificación: <strong>{clientIdSearch}</strong>
            </Alert>
          )}
          
          {petLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Cliente</th>
                  <th>Especie</th>
                  <th>Raza</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {pets.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      {petSearch ? 'No se encontraron mascotas con ese criterio' : 'No hay mascotas disponibles'}
                    </td>
                  </tr>
                ) : (
                  pets.map((pet) => (
                    <tr key={pet.id_pet}>
                      <td>{pet.id_pet}</td>
                      <td>{pet.name || 'N/A'}</td>
                      <td>{pet.name_client || 'N/A'}</td>
                      <td>
                        {pet.id_specie === 1 ? 'Perro' :
                         pet.id_specie === 2 ? 'Gato' :
                         pet.id_specie === 3 ? 'Ave' :
                         pet.id_specie === 4 ? 'Conejo' :
                         pet.id_specie === 5 ? 'Hamster' : 'Otro'}
                      </td>
                      <td>{pet.breed || 'N/A'}</td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleSelectPet(pet)}
                        >
                          Seleccionar
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClosePetModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de selección de item */}
      <Modal show={showItemModal} onHide={handleCloseItemModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Seleccionar Servicio de Grooming</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Form.Control
              type="text"
              placeholder="Buscar servicio por nombre o descripción..."
              value={itemSearch}
              onChange={handleItemSearch}
            />
          </div>
          {(useCartInEdit || !initialData) && cartDuplicateMsg && (
            <Alert variant="warning" className="mb-3 py-2" onClose={() => setCartDuplicateMsg('')} dismissible>
              {cartDuplicateMsg}
            </Alert>
          )}

          {itemLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Descripción</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      {itemSearch ? 'No se encontraron servicios con ese criterio' : 'No hay servicios de grooming disponibles'}
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const serviceName = item.description || item.name || 'N/A';
                    const price = formatPrice(item.price);
                    const displayText = price ? `${serviceName} ${price}` : serviceName;
                    
                    return (
                      <tr key={item.id_item}>
                        <td>{item.id_item}</td>
                        <td>{displayText}</td>
                        <td>
                          {initialData && isCompleted ? (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleSelectItem(item)}
                            >
                              Seleccionar
                            </Button>
                          ) : (
                            cartItems.some((x) => x.id_item === item.id_item) ? (
                              <Badge bg="secondary">Ya en el carrito</Badge>
                            ) : (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleAddToCart(item)}
                              >
                                <i className="fas fa-cart-plus me-1"></i>
                                Añadir al carrito
                              </Button>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseItemModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal últimos servicios del cliente */}
      <Modal show={showClientServicesModal} onHide={() => setShowClientServicesModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-history me-2"></i>
            Últimos servicios — {selectedPet?.name_client || 'Cliente'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {clientServicesLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" />
            </div>
          ) : clientServicesData.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="fas fa-inbox fa-2x mb-3"></i>
              <p className="mb-0">No hay citas registradas para este cliente.</p>
            </div>
          ) : (
            <div className="historial-list">
              {clientServicesData.map((apt, idx) => (
                <Card key={apt.id_appointment || idx} className="mb-3 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                      <div>
                        <strong>
                          {apt.start_at
                            ? new Date(apt.start_at).toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' })
                            : '—'}
                        </strong>
                        <Badge bg={apt.status_name === 'COMPLETADO' ? 'success' : apt.status_name === 'CANCELADO' ? 'danger' : 'secondary'} className="ms-2">
                          {apt.status_name || apt.id_status}
                        </Badge>
                      </div>
                      {apt.duration_min && <span className="text-muted">{apt.duration_min} min</span>}
                    </div>
                    <div className="text-muted small">
                      {apt.pet_name && <span className="me-2">Mascota: {apt.pet_name}</span>}
                      {(apt.description || apt.item_description) && (
                        <span>Servicio: {apt.description || apt.item_description}</span>
                      )}
                    </div>
                    {apt.services && apt.services.length > 0 && (
                      <ul className="list-unstyled mb-0 small mt-2">
                        {apt.services.map((srv, i) => (
                          <li key={srv.id_appointment_detail || i}>
                            {srv.item_description || 'Servicio'} — Cant: {srv.quantity || 1}
                            {srv.unit_price != null && (
                              <> · ${(parseFloat(srv.unit_price) || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClientServicesModal(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {/* Modal crear cliente */}
      <Modal show={showCreateClientModal} onHide={() => setShowCreateClientModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Crear Nuevo Cliente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomerForm
            onSubmit={handleCreateClient}
            loading={createClientLoading}
          />
        </Modal.Body>
      </Modal>

      {/* Modal opciones post-crear cliente */}
      <Modal show={showPostCreateOptionsModal} onHide={handleClosePostCreateOptions} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cliente creado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            ¿Qué deseas hacer ahora con {newlyCreatedClient ? `${newlyCreatedClient.name || ''} ${newlyCreatedClient.surname || ''}`.trim() : 'el cliente'}?
          </p>
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="primary" onClick={handlePostCreateChooseCita}>
              <i className="fas fa-calendar-check me-2"></i>
              Crear cita
            </Button>
            <Button variant="outline-primary" onClick={() => { setShowPostCreateOptionsModal(false); setShowCreatePetModal(true); }}>
              <i className="fas fa-paw me-2"></i>
              Crear mascota
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Modal crear mascota (con cliente precargado) */}
      <Modal show={showCreatePetModal} onHide={() => { setShowCreatePetModal(false); setNewlyCreatedClient(null); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Crear Mascota para {newlyCreatedClient ? `${newlyCreatedClient.name || ''} ${newlyCreatedClient.surname || ''}`.trim() : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {newlyCreatedClient && (
            <PetForm
              onSubmit={handleCreatePetFromNewClient}
              loading={createPetLoading}
              initialData={{ id_client: newlyCreatedClient.id_client }}
              submitText="Crear Mascota"
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Modal método de pago al marcar como PAGADA */}
      <Modal show={showPaymentMethodModal} onHide={() => setShowPaymentMethodModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-money-bill-wave me-2"></i>
            Seleccionar método de pago
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Al confirmar se generará la venta automáticamente con el método de pago seleccionado.
          </p>
          <Form.Group>
            <Form.Label>Método de pago *</Form.Label>
            <Form.Select
              value={selectedPaymentMethodModal}
              onChange={(e) => setSelectedPaymentMethodModal(e.target.value)}
            >
              <option value="">Seleccionar método de pago</option>
              {paymentMethods.map((pm) => (
                <option key={pm.id_payment_method || pm.id} value={pm.id_payment_method || pm.id}>
                  {pm.method_name || pm.payment_method_name || pm.name || `Método ${pm.id_payment_method || pm.id}`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentMethodModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmPaymentAndGenerateSale}
            disabled={!selectedPaymentMethodModal || loading}
          >
            {loading ? (
              <><Spinner animation="border" size="sm" className="me-2" /> Generando venta...</>
            ) : (
              <><i className="fas fa-check me-2"></i> Confirmar y generar venta</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AppointmentForm;
